// How the organisation's Post Opportunity page treats its jobs. The server
// is the source of truth (backend jobs/job-input.ts and jobs.service.ts):
// a job takes applications while its status is "Active" or "ACTIVE"; the
// web pauses one as "Inactive" and closes one as "CLOSED" (the app reads
// both as not taking applications). A job can be deleted only while nobody
// has applied to it -- the server answers 409 otherwise -- so after that it
// is closed instead, and its applications are kept.

const LIVE_STATUSES = new Set(["Active", "ACTIVE"]);
/** Closed or archived: closing it has nothing left to do. */
const ENDED_STATUSES = new Set(["CLOSED", "ARCHIVED"]);

export const PUBLISHED_STATUS = "Active";
export const PAUSED_STATUS = "Inactive";
export const CLOSED_STATUS = "CLOSED";

/** The server's limits on a job's title and description (job-input.ts). */
export const JOB_TITLE_MAX_LENGTH = 200;
export const JOB_DESCRIPTION_MAX_LENGTH = 10000;

// How a status reads, as the app shows it (mobile jobs/jobDisplay.js): a
// job paused on the web reads "Paused".
const STATUS_LABELS = {
  Active: "Active",
  ACTIVE: "Active",
  DRAFT: "Draft",
  Inactive: "Paused",
  CLOSED: "Closed",
  ARCHIVED: "Archived",
};

/** The job's status as people read it; any other status reads "Closed", as in the app. */
export function jobStatusLabel(job) {
  return STATUS_LABELS[job?.status] ?? "Closed";
}

/** Whether the job takes applications (its switch shows on). */
export function isJobLive(job) {
  return LIVE_STATUSES.has(job?.status);
}

/** The status the on/off switch asks for: a live job is paused, any other published. */
export function toggledJobStatus(job) {
  return isJobLive(job) ? PAUSED_STATUS : PUBLISHED_STATUS;
}

/**
 * Whether the job's closing day (YYYY-MM-DD) is before `today`, a day on
 * India's calendar as the server keeps it. The server lists such a job as
 * closed, whatever status it was given.
 */
export function closingDayPassed(job, today) {
  return Boolean(job?.toDate) && job.toDate < today;
}

/**
 * Whether the switch may change the job: pausing always; publishing only
 * while its closing day has not passed (it would still read as closed).
 */
export function canToggleJob(job, today) {
  return isJobLive(job) || !closingDayPassed(job, today);
}

/** Whether anyone has applied to the job, by the organisation's applications. */
export function jobHasApplicants(jobId, applicants) {
  return applicants.some((item) => String(item.jobId) === String(jobId));
}

/**
 * How the page offers to take a job down: "delete" while nobody has
 * applied, "close" once someone has, and null when it is already closed
 * or archived.
 */
export function jobRemovalAction(job, applicants) {
  if (!jobHasApplicants(job.id, applicants)) return "delete";
  return ENDED_STATUSES.has(job.status) ? null : "close";
}

/** The server refusing to delete a job someone has applied to. */
export function isDeleteRefused(error) {
  return error?.status === 409;
}

/** The jobs with one job's status replaced by the one the server answered. */
export function withJobStatus(jobs, jobId, status) {
  return jobs.map((job) =>
    String(job.id) === String(jobId) ? { ...job, status } : job,
  );
}

/** The jobs without one the server deleted. */
export function withoutJob(jobs, jobId) {
  return jobs.filter((job) => String(job.id) !== String(jobId));
}
